<!-- IdentityPage.vue -->
<template>
  <div class="min-h-screen mx-auto max-w-screen-xl">
    <IdentityTop class="mb-10" :data="data" :headlessContent="data?.headless_guide != null" />
    <ActivitySearchAndFilter
      :initial-params="searchParams"
      :featuredImage="data?.featured_image"
      :destinationName="data.city_display_name"
      @update:resultsCount="updateResultsCount"
      @error="handleError"
    />
    <div class="py-9">
      <HeadlessContent
        v-if="data?.headless_guide"
        :content="data.headless_guide"
        :cityDisplayName="data.city_display_name"
      ></HeadlessContent>
    </div>
    <div class="py-9">
      <TopLinks v-if="data?.top_links" :sections="data?.top_links"></TopLinks>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { saveItemInCookie, extractQuestionsAndAnswers } from '~/components/utils';
import { handleApiResponse } from '~/server/utils/utils';
import ActivitySearchAndFilter from '../components/ActivitiesSearchAndFilters.vue';
import IdentityTop from '../components/IdentityTop.vue';

const route = useRoute();
const { locale } = useI18n();
const emit = defineEmits(['passDataToHeader']);
const localePath = useLocalePath();
const linked_identity = route.query.i;

const searchParams = ref({
  city_name: route.params.city_n,
  identity_name: route.params.identity_name,
  lang: locale.value.split('-')[0],
  role: 'idt',
  highlighted_item: route.query.h,
  linked_identity: linked_identity,
});

const resultsCount = ref(0);

function updateResultsCount(count) {
  resultsCount.value = count;
}

function handleError(error) {
  // console.error(error);
}

// Fetch identity-specific data
const { data, error } = await handleApiResponse(`/api/explore`, {
  params: {
    type: 'identity',
    city_name: route.params.city_n,
    identity_name: route.params.identity_name,
    lang: locale.value.split('-')[0],
  },
});

if (data.value) {
  emit('passDataToHeader', {
    popularSearches: data?.value?.top_links,
    currentPageName: data?.value?.title,
    cityID: data?.value?.city?.destination_id,
    role: data?.value?.city?.role,
  });
  // Save destination key in cookie to show the correct destination in the search bar and homepage
  saveItemInCookie('RVDestinations',data?.value?.identity?.destination_key);
}

// To understand what each structure means, visit https://developers.google.com/search/docs/appearance/structured-data/search-gallery

// To be implemented in the future
// const productData = {
//   '@context': 'https://schema.org',
//   // Don't combine multiple types in a single array - each should be separate
//   '@type': 'Product',

//   name: data.value?.title,
//   description: data.value?.top_description,
//   image: data.value?.identity?.featured_image,
//   url: `https://tourscanner.com${data.value?.canonical}`,

//   // Address should be part of the location property for a product/place
//   location: {
//     '@type': 'Place',
//     address: {
//       '@type': 'PostalAddress',
//       addressLocality: data.value?.city?.name,
//       addressCountry: data.value?.country_name,
//     },
//   },

//   // Aggregate rating should be directly under the product
//   aggregateRating: {
//     '@type': 'AggregateRating',
//     ratingValue: data.value?.reviews_average,
//     reviewCount: data.value?.reviews_count,
//     bestRating: 5,
//   },
// };

const breadcrumbData = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: 'https://tourscanner.com/',
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: data.value?.city?.name,
      item: `https://tourscanner.com/s/${data.value?.canonical.split('/')[2]}`,
    },
    {
      '@type': 'ListItem',
      position: 3,
      name: data.value?.identity?.name,
      item: `https://tourscanner.com${data.value?.canonical}`,
    },
  ],
};

// CANNOT USE IT. GOOGLE DOES NOT ALLOW FAQPAGE TO BE USED IF NOT GOV OR HEALTH SITE. Maybe will change in the future?
// Extracting the question and answers from the guide, which is html in string form.
// Alternatively questions can be accessed from data.value.headless_guide.index, but doesn't provide answers.
// const FA_Question_Answers = data.value.headless_guide.guide;
// const extractedAnswers = extractQuestionsAndAnswers(FA_Question_Answers);

// const faqData = {
//   '@context': 'https://schema.org',
//   '@type': 'FAQPage',
//   mainEntity: extractedAnswers.map((faq) => ({
//     '@type': 'Question',
//     name: faq.question,
//     acceptedAnswer: {
//       '@type': 'Answer',
//       text: faq.answer,
//     },
//   })),
// };

// Pending. A required field is needed but do not have info.
// const imageMetaData = [
//   {
//     '@context': 'https://schema.org/',
//     '@type': 'ImageObject',
//     contentUrl: data?.value?.featured_image,
//   },
//   {
//     '@context': 'https://schema.org/',
//     '@type': 'ImageObject',
//     contentUrl: data?.value?.city?.featured_image,
//   },
//   {
//     '@context': 'https://schema.org/',
//     '@type': 'ImageObject',
//     contentUrl: data?.value?.headless_guide?.featured_image_link,
//   },
// ];

// Pending. Need to add functionality to fetch from backend
// const reviewSnippetMetaData =[]

useHead({
  title: data?.value?.meta_title,
  meta: [
    { name: 'title', content: data?.value?.meta_title },
    { name: 'description', content: data?.value?.meta_description },
    { name: 'keywords', content: data?.value?.meta_keywords },
    {
      name: 'robots',
      content: data?.value?.isIndexable
        ? 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
        : 'noindex, follow',
    },
  ],
  script: [
    // To be implemented in the future
    // {
    //   type: 'application/ld+json',
    //   innerHTML: JSON.stringify(productData),
    // },
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify(breadcrumbData),
    },
    // {
    //   type: 'application/ld+json',
    //   innerHTML: JSON.stringify(faqData),
    // },
    // {
    //   type: 'application/ld+json',
    //   innerHTML: JSON.stringify(imageMetaData),
    // },
    // {
    //   type: 'application/ld+json',
    //   innerHTML: JSON.stringify(reviewSnippetMetaData),
    // },
  ],
});

const rule = useRobotsRule();
rule.value = data.value.isIndexable
  ? 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
  : 'noindex, follow';

const getArticleAuthors = computed(() => {
  let SEOauthors = [];
  if (data.value && data?.value?.headless_guide) {
    if (data.value.headless_guide.authors) {
      for (const author of data?.value?.headless_guide.authors) {
        SEOauthors.push(localePath(author.url));
      }
    }
  }
  return SEOauthors;
});

useSeoMeta({
  ogType: 'website',
  title: data.value?.meta_title,
  ogTitle: data.value?.meta_title,
  description: data.value?.meta_description,
  ogDescription: data.value?.meta_description,
  ogImage: data.value?.featured_image,
  keywords: data.value?.meta_keywords,
  twitterCard: 'summary_large_image',
  articleAuthor: getArticleAuthors.value,
});
</script>
